import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { X, Sparkles, TrendingUp, Heart } from "lucide-react";
import { cn } from "@/lib/utils";

export const RecapModal = ({ onClose }) => {
  const { token } = useAuth();
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    checkRecap();
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const checkRecap = async () => {
    try {
      const { data } = await axios.get(`${API}/recap/today`, { headers });
      if (data.recap && !data.recap.seen) {
        setRecap(data.recap);
        setLoading(false);
      } else if (!data.recap) {
        setGenerating(true);
        setLoading(false);
        generateRecap();
      } else {
        onClose();
      }
    } catch {
      onClose();
    }
  };

  const generateRecap = async () => {
    try {
      const { data } = await axios.post(`${API}/recap/generate`, {}, { headers });
      if (data.recap === null || data.reason === "no_conversations") {
        onClose();
        return;
      }
      setRecap(data);
      setGenerating(false);
    } catch {
      onClose();
    }
  };

  const dismiss = async () => {
    try {
      await axios.post(`${API}/recap/seen`, {}, { headers });
    } catch { /* silent */ }
    onClose();
  };

  if (loading || (!recap && !generating)) return null;

  const THEME_COLORS = [
    "bg-primary/10 text-primary",
    "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
    "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
    "bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300",
    "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300",
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={dismiss}>
      <div
        data-testid="recap-modal"
        className="relative w-full max-w-md mx-4 bg-card rounded-3xl border border-border/50 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
        onClick={e => e.stopPropagation()}
      >
        {/* Header gradient strip */}
        <div className="h-1.5 bg-gradient-to-r from-primary via-primary/60 to-primary/20" />

        <div className="p-6 space-y-5">
          {/* Title */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="font-heading text-lg font-medium">Your Daily Recap</h2>
                <p className="text-xs text-muted-foreground">
                  {recap?.conversation_count || 0} conversation{recap?.conversation_count !== 1 ? "s" : ""} reflected on
                </p>
              </div>
            </div>
            <button
              data-testid="recap-close"
              onClick={dismiss}
              className="p-2 rounded-full hover:bg-muted/50 text-muted-foreground transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {generating ? (
            <div className="py-8 flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-muted-foreground">Reflecting on your day...</p>
            </div>
          ) : recap ? (
            <>
              {/* Summary */}
              <p data-testid="recap-summary" className="text-sm text-foreground/80 leading-relaxed">
                {recap.summary}
              </p>

              {/* Themes */}
              {recap.themes?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Themes</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {recap.themes.map((theme, i) => (
                      <span
                        key={i}
                        data-testid={`recap-theme-${i}`}
                        className={cn("px-3 py-1 rounded-full text-xs font-medium", THEME_COLORS[i % THEME_COLORS.length])}
                      >
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Emotions */}
              {recap.emotions?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Emotional Tone</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {recap.emotions.map((emo, i) => (
                      <span key={i} className="px-3 py-1 rounded-full text-xs bg-muted text-muted-foreground capitalize">
                        {emo}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Encouragement */}
              {recap.encouragement && (
                <div className="bg-primary/5 border border-primary/10 rounded-2xl p-4">
                  <p data-testid="recap-encouragement" className="text-sm text-foreground/70 italic leading-relaxed">
                    "{recap.encouragement}"
                  </p>
                </div>
              )}
            </>
          ) : null}

          {/* Dismiss button */}
          <button
            data-testid="recap-dismiss"
            onClick={dismiss}
            className="w-full py-3 rounded-2xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition-all"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};
